import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Observable, Subscription, map, startWith } from 'rxjs';
import { AdvancedSearchConfig } from '@huge-monorepo/hip/domain';
import { ToolsService } from '@huge-monorepo/hip/services';

@Component({
  selector: 'tools-advanced-search',
  templateUrl: './advanced-search.component.html',
  styleUrls: ['./advanced-search.component.scss'],
})
export class AdvancedSearchComponent implements OnInit, OnDestroy {
  //Possible changes to make
  //-> Allow inputing a custom fuzzy control name so that the output has the exact field name you require

  @Input() advancedSearchConfig: AdvancedSearchConfig = undefined;
  @Input() searchReminderText: string = 'Use the filters to start searching. The table has not been loaded yet.';
  @Output() searchExecuted = new EventEmitter<any>();

  firstSearch: boolean = false;
  advancedSearchForm: UntypedFormGroup = new UntypedFormGroup({});
  datesForm: UntypedFormGroup = new UntypedFormGroup({});
  fuzzyControl = new UntypedFormControl('');
  formSubs: Subscription[] = [];
  allSelectedInDropDowns: {
    [id: string]: UntypedFormControl;
  } = {}; //Holds the form controls for the select all mat checkboxes
  filteredDropDownValues: {
    [id: string]: Observable<any[]>;
  } = {};
  originalDropDownValues: {
    [id: string]: any[];
  } = {};
  dropDownSearchControls: {
    [id: string]: UntypedFormControl;
  } = {};

  constructor(private tools: ToolsService) {}
  ngOnDestroy(): void {
    this.formSubs.forEach(s => s.unsubscribe());
  }

  ngOnInit(): void {
    //Add date controls to the dates form group.
    if (this.advancedSearchConfig.showDateRange) {
      this.datesForm.addControl('startDateControl', new UntypedFormControl(this.tools.FormatStartDate(this.tools.GetStartOfMonth(new Date()).toISOString()))),
        this.datesForm.addControl('endDateControl', new UntypedFormControl(this.tools.GetEndOfDay(new Date()).toISOString()));
    }

    //Add a control to the advancedSearchForm group for every control added in config
    this.advancedSearchConfig.formControls.forEach((control) => {
      this.advancedSearchForm.addControl(
        control.controlName,
        new UntypedFormControl(control.defaultValue ? control.defaultValue : control.type == 'dropDown' && control.allowMultipleSelect ? [] : null),
      );

      //If the control is of type drop down and multi select. We add a control for the select all option.
      if (control.type == 'dropDown' && control.allowMultipleSelect) {
        this.allSelectedInDropDowns[control.controlName] = new UntypedFormControl(false);
        this.originalDropDownValues[control.controlName] = control.dropDownValues;
        this.dropDownSearchControls[control.controlName] = new UntypedFormControl('');

        this.filteredDropDownValues[control.controlName] = this.dropDownSearchControls[control.controlName].valueChanges.pipe(
          startWith(''),
          map((value) => this._filter(value || '', control.controlName)),
        );
      }
    });

    //Add a value change subscription to the fuzzy search input
    let fSub = this.fuzzyControl.valueChanges.subscribe((value) => {
      if (value) {
        if (!this.advancedSearchForm.disabled) {
          //If fuzzy input is not empty, disable all other inputs. (Excluding date inputs)
          this.advancedSearchForm.disable();
        }
      } else if (this.advancedSearchForm.disabled) {
        //If fuzzy input is empty enable all other inputs.
        this.advancedSearchForm.enable();
      }
    });

    //Add a value change subscription to every input other than fuzzy and dates.
    let aSub = this.advancedSearchForm.valueChanges.subscribe((changes) => {
      let anyPopulated = false; //Variable checks if any input other than fuzzy and dates have a value.

      for (let key in changes) {
        let val = changes[key];

        if (val && val.length > 0) {
          anyPopulated = true;
          break;
        }
      }

      //If any of the inputs do have a value and fuzzy is enabled. Disable fuzzy
      if (anyPopulated && !this.fuzzyControl.disabled) this.fuzzyControl.disable();

      //If no values in inputs, enable fuzzy.
      if (!anyPopulated) this.fuzzyControl.enable();
    });

    this.formSubs.push(...[fSub,aSub]);
  }

  updateStartDateTime(eve) {
    this.datesForm.controls['startDateControl'].setValue(eve);
  }

  updateEndDateTime(eve) {
    this.datesForm.controls['endDateControl'].setValue(eve);
  }

  setAll(controlName: string) {
    //Function enables or disables all in multiselect dropdown
    if (!this.allSelectedInDropDowns[controlName].value) {
      this.advancedSearchForm.controls[controlName].reset();
    } else {
      this.advancedSearchForm.controls[controlName].setValue(
        this.advancedSearchConfig?.formControls.find((control) => control.controlName == controlName).dropDownValues.map((value) => value.value),
      );
    }
  }

  reset() {
    //Resets all forms to default
    this.advancedSearchForm.reset();
    this.fuzzyControl.reset();
  }

  submit() {
    //Triggered when search clicked. Emits values to parent.
    let output = {};
    output = Object.assign(
      {},
      this.datesForm ? this.datesForm.value : {},
      this.advancedSearchForm.enabled ? this.advancedSearchForm.value : {},
      this.fuzzyControl.enabled
        ? {
            FuzzySearch: this.fuzzyControl.value,
          }
        : {},
    );

    this.firstSearch = true;
    this.searchExecuted.emit(output);
  }

  private _filter(value: string, controlName: string): string[] {
    const filterValue = value.toLowerCase();
    return this.originalDropDownValues[controlName].filter((option) => option.label.toLowerCase().includes(filterValue));
  }
}
